import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import Breadcrumb from '../components/Breadcrumb';
import Pagination from '../components/main/Pagination';
import useDocumentsApi from '../api/useDocumentsApi';
import { debounce } from '../helper/debounce';
import dayjs from 'dayjs';
import { Avatar, Dialog } from '@mui/material';
import CommonLoader from '../components/loaders/CommonLoader';
import { MdClose } from 'react-icons/md';
import useUsersApi from '../api/useUsersApi';
import { toast } from 'react-toastify';
import 'swiper/css';
import { Swiper, SwiperSlide } from 'swiper/react';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import 'react-photo-view/dist/react-photo-view.css';
import { useAuth } from '../contexts/AuthContext';
import CallConfirmationDialog from '../components/modals/CallConfirmationDialog';
import WhatsAppConfirmationDialog from '../components/modals/WhatsAppConfirmationDialog';

const DocVerificationsPage = () => {
    const [selectedDoc, setSelectedDoc] = useState(null);
    const [selectedTab, setSelectedTab] = useState('aadhaarcard_info');
    const [selectedStatus, setSelectedStatus] = useState('-1');
    const [modalOpen, setModalOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const { isActionAllowed } = useAuth();
    const { getDocsData, loading } = useDocumentsApi();
    const { loading: verifyLoading, verifyDocuments, makeCall, sendWhatsAppNotification } = useUsersApi();
    const [callTarget, setCallTarget] = useState(null);   // { user_id, user_name, phone_number }
    const [callDialogOpen, setCallDialogOpen] = useState(false);
    const [callLoading, setCallLoading] = useState(false);
    const [whatsappDialogOpen, setWhatsappDialogOpen] = useState(false);
    const [whatsappLoading, setWhatsappLoading] = useState(false);
    const mainSwiperRef = useRef(null);
    const handleThumbnailClick = (index) => {
        setActiveIndex(index);
        if (mainSwiperRef.current) {
            mainSwiperRef.current.swiper.slideTo(index);
        }
    };

    const [data, setData] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const [total_pages, setTotalPages] = useState(1);
    const [openApprove, setOpenApprove] = useState(false);
    const [openReject, setOpenReject] = useState(false);
    const [reason, setReason] = useState("");

    const fetchDocs = async (page = 1, search = '') => {
        const res = await getDocsData(page, search, selectedTab, selectedStatus);
        setData(res.data.payload.documents);
        setTotalPages(res.data.payload.total_pages);
    };

    const debouncedfetchDocs = useCallback(
        debounce((value) => {
            fetchDocs(1, value);
            setPage(1);
        }, 500),
        []
    );

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        debouncedfetchDocs(value);
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    useEffect(() => {
        fetchDocs(page, searchTerm);
    }, [page, selectedStatus, selectedTab]);

    const handleApprove = (docInfo) => {
        setSelectedDoc(docInfo);
        setOpenApprove(true)
    };

    const handleReject = (docInfo) => {
        setReason("")
        setSelectedDoc(docInfo);
        setOpenReject(true)
    };

    const handleView = (doc) => {
        setSelectedDoc(doc);
    };

    const handleCallClick = (doc) => {
        setCallTarget({
            user_id: doc.user_id?._id,
            user_name: doc.user_id?.user_name,
            phone_number: doc.user_id?.phone_number,
        });
        setCallDialogOpen(true);
    };

    const handleWhatsAppClick = (doc) => {
        setCallTarget({
            user_id: doc.user_id?._id,
            user_name: doc.user_id?.user_name,
            phone_number: doc.user_id?.phone_number,
        });
        setWhatsappDialogOpen(true);
    };

    const handleCallConfirm = async () => {
        if (!callTarget?.phone_number) return;
        try {
            setCallLoading(true);
            await makeCall(callTarget.phone_number);
            toast.success('Call initiated successfully!');
        } catch (err) {
            // error handled by useApi hook
        } finally {
            setCallLoading(false);
            setCallDialogOpen(false);
        }
    };

    const handleWhatsAppConfirm = async () => {
        if (!callTarget?.phone_number) return;
        try {
            setWhatsappLoading(true);
            await sendWhatsAppNotification(callTarget.phone_number);
            toast.success('WhatsApp notification sent successfully!');
        } catch (err) {
            // error handled by useApi hook
        } finally {
            setWhatsappLoading(false);
            setWhatsappDialogOpen(false);
        }
    };

    const openModal = (details) => {
        setSelectedDoc(details);
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
    };

    const getStatusClass = (status) => {
        switch (status) {
            case 2:
                return 'bg-success text-white'; // Green
            case 3:
                return 'bg-danger text-white'; // Red
            case 0:
                return 'bg-warning text-black'; // Yellow
            case 1:
                return 'bg-primary text-white';
            default:
                return 'bg-light text-dark'; // Default
        }
    };

    const getTabClass = (tab) => {
        return selectedTab === tab
            ? 'bg-primary text-white' // Active Tab - Blue
            : 'bg-light text-dark'; // Inactive Tab
    };

    const handleApproveSubmit = async () => {
        setOpenApprove(false);
        const payload = {
            "documentId": selectedDoc._id,
            "documentType": selectedDoc.documentType,
            "document_status": "2"
        }

        await verifyDocuments(payload);
        fetchDocs(page, searchTerm)
    };

    const handleRejectSubmit = async () => {
        if (!reason.trim()) {
            toast.error("Please enter a rejection reason.");
            return;
        }
        const payload = {
            "documentId": selectedDoc._id,
            "documentType": selectedDoc.documentType,
            "document_status": "3",
            "reason": reason
        }
        setOpenReject(false);
        await verifyDocuments(payload);
        fetchDocs(page, searchTerm)
    };

    return (
        <>
            <div>
                <Breadcrumb title="Document Verifications" />

                {/* Custom Tabs for Document Types */}
                <div className="custom-tabs">
                    {['aadhaarcard_info', 'driver_license_info', 'driver_car_info', 'other_docs'].map((tab) => (
                        <div
                            key={tab}
                            className={`tab-item ${getTabClass(tab)}`}
                            onClick={() => setSelectedTab(tab)}
                        >
                            {tab === 'all' ? 'All' : tab
                                .replace(/([a-z])([A-Z])/g, '$1 $2')  // Add space before capital letters
                                .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2') // Handle consecutive uppercase words like "PDFViewer"
                                .replace(/Info$/, ' Info')             // Add space before 'Info' suffix if needed
                                .replace(/^\w/, (c) => c.toUpperCase()) // Capitalize the first letter
                            }
                        </div>
                    ))}
                </div>

                {/* Status Filter Buttons */}




                <div className="card radius-12 mt-4">
                    <div className="card-body ">
                        <div className='d-flex align-items-center justify-content-between flex-wrap gap-2'>
                            <form className="navbar-search">
                                <input
                                    type="text"
                                    className="bg-base h-25-px"
                                    name="search"
                                    placeholder="Search"
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                />
                                <Icon icon="ion:search-outline" className="icon" />
                            </form>
                            <div className="status-filters d-flex gap-2 flex-wrap">
                                {['all', 'pending', 'review', 'verified', 'unverified'].map((status, index) => {
                                    const statusColors = {
                                        all: 'bg-dark text-white border',
                                        pending: 'bg-warning text-black',
                                        review: 'bg-primary text-white',
                                        verified: 'bg-success text-white',
                                        unverified: 'bg-danger text-white',
                                    };

                                    const mappedValue = status === 'all' ? -1 : index - 1;
                                    const is_live = selectedStatus === mappedValue;
                                    const baseClass = is_live ? 'fw-bold border-2' : 'bg-opacity-25 text-opacity-75 text-black border';

                                    return (
                                        <button
                                            key={status}
                                            className={`btn btn-sm py-1 px-3 ${statusColors[status]} ${baseClass}`}
                                            style={{ fontSize: '0.75rem', height: '30px' }}
                                            onClick={() => setSelectedStatus(mappedValue)}
                                        >
                                            {status.charAt(0).toUpperCase() + status.slice(1)}
                                        </button>
                                    );
                                })}
                            </div>

                        </div>
                        <div className="py-24">
                            {loading ? (
                                <CommonLoader />
                            ) : (
                                <>
                                    <div className="table-responsive scroll-sm">
                                        <table className="table bordered-table sm-table mb-0">
                                            <thead>
                                                <tr>
                                                    <th className="text-start">User</th>
                                                    <th className="text-start">Admin</th>
                                                    <th className="text-center">Phone</th>
                                                    <th className="text-center">Document</th>
                                                    <th className="text-center">Doc No.</th>
                                                    <th className="text-center">Submitted</th>
                                                    <th className="text-center">Status</th>
                                                    <th className="text-center">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {data.length > 0 ? (data.map((doc) => (
                                                    <tr key={doc._id}>
                                                        <td className='text-start'>
                                                            <div className="d-flex align-items-center">
                                                                <Avatar
                                                                    src={doc.user_id?.profile_photo}
                                                                    alt=""
                                                                    className="w-40-px h-40-px rounded-circle me-12"
                                                                />
                                                                <span className="text-md text-secondary-light">{doc.user_id?.user_name || "-"}</span>
                                                            </div>
                                                        </td>
                                                        <td className="text-center">{doc.user_id?.parent_user_id?.user_name || '-'}</td>
                                                        <td className="text-center">{doc.user_id?.phone_number || '-'}</td>
                                                        <td className="text-center">{doc.documentType || '-'}</td>
                                                        <td className="text-center">{doc.number || '-'}</td>
                                                        <td className="text-center">{dayjs(doc.updatedAt).format('DD MMM YYYY hh:mm A')}</td>
                                                        <td className="text-center">
                                                            <span
                                                                className={`badge text-xs ${getStatusClass(doc.document_status)}`}
                                                            >
                                                                {['Pending', 'Review', 'Verified', 'Unverified'][doc.document_status]}
                                                            </span>
                                                        </td>
                                                        <td className="text-center">
                                                            <div className="d-flex justify-content-center gap-2">
                                                                {isActionAllowed('DocVerifications', 'view') ? (
                                                                    <button
                                                                        onClick={() => openModal(doc)}
                                                                        className="bg-info-focus text-info-600 w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                                    >
                                                                        <Icon icon="majesticons:eye-line" className="text-xl" />
                                                                    </button>
                                                                ) : null}
                                                                {isActionAllowed('DocVerifications', 'edit') ? (
                                                                    <button
                                                                        onClick={() => handleApprove(doc)}
                                                                        className="bg-success-focus text-success-600 w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                                    >
                                                                        <Icon icon="mdi:check-bold" className="text-xl" />
                                                                    </button>
                                                                ) : null}
                                                                {isActionAllowed('DocVerifications', 'edit') ? (
                                                                    <button
                                                                        onClick={() => handleReject(doc)}
                                                                        className="bg-danger-focus text-danger-600 w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                                    >
                                                                        <Icon icon="mdi:close-thick" className="text-xl" />
                                                                    </button>
                                                                ) : null}
                                                                    <button
                                                                        onClick={() => handleCallClick(doc)}
                                                                        title="Call"
                                                                        className="bg-success-focus text-success-600 w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                                    >
                                                                        <Icon icon="akar-icons:phone" className="text-xl" />
                                                                    </button>
                                                                    <button
                                                                        onClick={() => handleWhatsAppClick(doc)}
                                                                        title="WhatsApp"
                                                                        className="bg-info-focus text-info-600 w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                                    >
                                                                        <Icon icon="ic:baseline-whatsapp" className="text-xl" />
                                                                    </button>
                                                                <button
                                                                    onClick={() => window.open(`/users-list/${doc.user_id?._id}`, '_blank')}
                                                                    className="bg-info-focus text-info-600 w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                                >
                                                                    <Icon icon="mdi:user" className="text-xl" />
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="8" className="text-center py-4">No Documents Found</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
                                        <span>Page {page} of {total_pages}</span>
                                        <Pagination
                                            total_pages={total_pages}
                                            current_page={page}
                                            onPageChange={handlePageChange}
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
                <Dialog
                    open={openApprove}
                    onClose={() => setOpenApprove(false)}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{ sx: { borderRadius: '16px' } }}
                >
                    <div className="card mb-0">
                        <div className="card-header d-flex justify-content-between align-items-center">
                            <h6 className="card-title mb-0">Approve Document</h6>
                            <button type="button" className="btn btn-sm btn-light p-1 d-flex justify-content-center align-items-center" onClick={() => setOpenApprove(false)}>
                                <MdClose size={18} />
                            </button>
                        </div>

                        <div className="card-body">
                            <p className="text-center">
                                Are you sure want to approve this document ?
                            </p>
                            <div className="d-flex justify-content-center align-items-center mt-3">
                                <button onClick={handleApproveSubmit}
                                    className="btn btn-primary border border-primary-600 text-md px-48 py-12 radius-8">
                                    Approve
                                </button>
                            </div>
                        </div>
                    </div>
                </Dialog>

                <Dialog
                    open={openReject}
                    onClose={() => setOpenReject(false)}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{ sx: { borderRadius: '16px' } }}
                >
                    <div className="card mb-0">
                        <div className="card-header d-flex justify-content-between align-items-center">
                            <h6 className="card-title mb-0">Reject Document</h6>
                            <button type="button" className="btn btn-sm btn-light p-1 d-flex justify-content-center align-items-center" onClick={() => setOpenReject(false)}>
                                <MdClose size={18} />
                            </button>
                        </div>

                        <div className="card-body">
                            <p className="">

                                <textarea
                                    className="form-control"
                                    rows="4"
                                    placeholder="Write reason"
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                />
                            </p>
                            <div className="d-flex justify-content-center align-items-center mt-3">
                                <button onClick={handleRejectSubmit} className="btn btn-danger border border-danger-600 text-md px-48 py-12 radius-8">
                                    Reject
                                </button>
                            </div>
                        </div>
                    </div>
                </Dialog>

                {selectedDoc && <Dialog
                    open={modalOpen}
                    onClose={closeModal}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{ sx: { borderRadius: '16px' } }}
                >
                    <div className="card mb-0">
                        <div className="card-header d-flex justify-content-between align-items-center">
                            <h6 className="card-title mb-0">Document Details</h6>
                            <button type="button" className="btn btn-sm btn-light p-1" onClick={closeModal}>
                                <MdClose size={18} />
                            </button>
                        </div>

                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <span>
                                    Document No. {selectedDoc.number || selectedDoc?.details}
                                </span>
                                {selectedDoc.document_status === 0 ? (
                                    <span className="badge bg-white text-dark">Pending</span>
                                ) : selectedDoc.document_status === 1 ? (
                                    <span className="badge bg-warning text-dark">In Review</span>
                                ) : selectedDoc.document_status === 2 ? (
                                    <span className="badge bg-success">Verified</span>
                                ) : selectedDoc.document_status === 3 ? (
                                    <span className="badge bg-danger">Rejected</span>
                                ) : null}
                            </div>

                            <Swiper
                                spaceBetween={10}
                                slidesPerView={1}
                                pagination={{ clickable: true }}
                                onSlideChange={(swiper) => setActiveIndex(swiper.activeIndex)}
                                className="main-swiper mt-3"
                                ref={mainSwiperRef}
                            >
                                {(selectedDoc?.images
                                    ? selectedDoc.images
                                    : [...(selectedDoc?.carImages || []), ...(selectedDoc?.carRcImages || [])]
                                )?.map((imgUrl, index) => (
                                    <SwiperSlide key={index}>
                                        <PhotoProvider>
                                            <PhotoView src={imgUrl}>
                                                <img
                                                    src={imgUrl}
                                                    alt={`Image ${index + 1}`}
                                                    className="w-100"
                                                    style={{ objectFit: 'contain', height: '400px' }}
                                                />
                                            </PhotoView>
                                        </PhotoProvider>
                                    </SwiperSlide>
                                ))}
                            </Swiper>
                            <div
                                className="thumbnail-container"
                                style={{
                                    display: 'flex',
                                    overflowX: 'auto',
                                    padding: '10px 0',
                                    gap: '10px',
                                }}
                            >
                                {(selectedDoc?.images
                                    ? selectedDoc.images
                                    : [...(selectedDoc?.carImages || []), ...(selectedDoc?.carRcImages || [])]
                                )?.map((imgUrl, index) => (
                                    <div
                                        key={index}
                                        className="thumbnail-item"
                                        style={{
                                            width: '50px',
                                            height: '50px',
                                            cursor: 'pointer',
                                            border: activeIndex === index ? '2px solid #007bff' : 'none',
                                            borderRadius: "10px"
                                        }}
                                        onClick={() => handleThumbnailClick(index)}
                                    >
                                        <img
                                            src={imgUrl}
                                            alt={`Thumbnail ${index + 1}`}
                                            style={{
                                                objectFit: 'cover',
                                                width: '100%',
                                                height: '100%',
                                                borderRadius: '10px',
                                                padding: activeIndex === index ? '1px' : '',
                                                transition: 'filter 0.3s ease',
                                            }}
                                        />

                                    </div>
                                ))}
                                {selectedDoc?.images?.length === 0 && <>No documents found!</>}
                            </div>
                        </div>
                    </div>
                </Dialog>
                }
            </div>

            {/* Call Confirmation Dialog */}
            <CallConfirmationDialog
                open={callDialogOpen}
                onClose={() => setCallDialogOpen(false)}
                onConfirm={handleCallConfirm}
                userName={callTarget?.user_name}
                phoneNumber={callTarget?.phone_number}
                loading={callLoading}
            />

            <WhatsAppConfirmationDialog
                open={whatsappDialogOpen}
                onClose={() => setWhatsappDialogOpen(false)}
                onConfirm={handleWhatsAppConfirm}
                userName={callTarget?.user_name}
                phoneNumber={callTarget?.phone_number}
                loading={whatsappLoading}
            />
        </>
    );
};

export default DocVerificationsPage;
